import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  checkActiveProSubscription,
  fetchSubscriptionStoreProducts,
  getIapProductIds,
  purchaseNativeSubscription,
  restoreNativePurchases,
} from '@/utils/nativeSubscriptions';

export const PRIVACY_POLICY_URL = 'https://www.re-build-me.com/p/privacy-policy-for-re-build-me-pro.html';
export const TERMS_OF_USE_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';

function normalizeProductPrice(product) {
  if (!product) return '';
  return String(product.priceString || product.localizedPrice || product.displayPrice || '').trim();
}

function normalizeProductTitle(product, fallback) {
  if (!product) return fallback;
  return String(product.title || product.name || fallback || '').trim() || fallback;
}

function normalizePlanMeta(plan, product, fallbackTitle) {
  const title = normalizeProductTitle(product, fallbackTitle);
  const price = normalizeProductPrice(product);
  const duration = plan === 'annual' ? '1 year' : '1 month';
  const trialText = String(product?.introPricePaymentModeIOS || product?.introductoryPrice || '').trim();
  const offerText = String(product?.description || '').trim();
  return {
    plan,
    title,
    price,
    duration,
    trialText,
    offerText,
  };
}

function LegalLinks({ centered = false }) {
  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-2 ${centered ? 'justify-center' : ''}`}>
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 underline underline-offset-2"
      >
        Privacy Policy
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
      <a
        href={TERMS_OF_USE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 underline underline-offset-2"
      >
        Terms of Use
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    </div>
  );
}

export function SubscriptionLegalCopy({ centered = false, className = '' }) {
  return (
    <div className={`space-y-2 font-body text-xs leading-relaxed text-secondary ${className}`.trim()}>
      <p>
        Auto-renewable subscriptions unlock Pro access. Monthly renews every 1 month and annual renews every 1 year until canceled in your Apple ID subscriptions settings.
      </p>
      <LegalLinks centered={centered} />
    </div>
  );
}

export function NativeSubscriptionOptions({
  firebaseUid,
  onActivated,
  onBusyChange,
  showRestore = true,
  centered = false,
  buttonClassName = '',
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [monthlyProduct, setMonthlyProduct] = useState(null);
  const [annualProduct, setAnnualProduct] = useState(null);

  const setBusyState = useCallback((next) => {
    setBusy(next);
    onBusyChange?.(next);
  }, [onBusyChange]);

  const refreshProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { monthlyProduct: monthly, annualProduct: annual } = await fetchSubscriptionStoreProducts();
      setMonthlyProduct(monthly || null);
      setAnnualProduct(annual || null);
    } catch (e) {
      console.warn('fetchSubscriptionStoreProducts:', e);
      const ids = getIapProductIds();
      const idHint =
        ids.monthly && ids.annual
          ? ` This build requests product IDs: ${ids.monthly}, ${ids.annual}.`
          : ' Subscription product IDs are not set for this build.';
      toast.error(
        `Could not load subscription options.${idHint} In Xcode enable the In-App Purchase capability, match App Store Connect IDs exactly, sign Paid Applications Agreement, and use a Sandbox Apple ID on device.`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshProducts();
  }, [refreshProducts]);

  const tryUnlock = useCallback(async () => {
    if (!firebaseUid) return false;
    const ok = await checkActiveProSubscription(firebaseUid);
    if (ok) onActivated?.();
    return ok;
  }, [firebaseUid, onActivated]);

  const handlePurchase = useCallback(async (plan) => {
    if (!firebaseUid) return;
    setBusyState(true);
    try {
      await purchaseNativeSubscription(plan, firebaseUid);
      let ok = await tryUnlock();
      if (!ok) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        ok = await tryUnlock();
      }
      if (ok) toast.success('Subscription active.');
      else toast.message('Purchase completed. Tap Restore purchases if activation is delayed.');
    } catch (e) {
      const msg = String(e?.message || e || 'Purchase failed');
      if (/cancel/i.test(msg)) toast.message('Purchase cancelled');
      else toast.error(msg);
    } finally {
      setBusyState(false);
    }
  }, [firebaseUid, setBusyState, tryUnlock]);

  const handleRestore = useCallback(async () => {
    setBusyState(true);
    try {
      await restoreNativePurchases();
      const ok = await tryUnlock();
      if (ok) toast.success('Purchases restored.');
      else toast.message('No active subscription found for this Apple/Google account.');
    } catch (e) {
      toast.error(String(e?.message || e || 'Restore failed'));
    } finally {
      setBusyState(false);
    }
  }, [setBusyState, tryUnlock]);

  const plans = useMemo(() => ([
    normalizePlanMeta('monthly', monthlyProduct, 'Monthly Subscription'),
    normalizePlanMeta('annual', annualProduct, 'Annual Subscription'),
  ]), [annualProduct, monthlyProduct]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-secondary font-body text-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading subscription options…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {plans.map((plan) => (
        <button
          key={plan.plan}
          type="button"
          disabled={busy || !firebaseUid}
          onClick={() => void handlePurchase(plan.plan)}
          className={buttonClassName || 'w-full rounded-[1.5rem] border border-theme-secondary bg-app px-4 py-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 disabled:opacity-50'}
        >
          <span className={`flex w-full ${centered ? 'justify-center' : 'justify-between'} gap-3`}>
            <span className={`${centered ? 'items-center text-center' : ''} flex min-w-0 flex-1 flex-col`}>
              <span className="font-body text-lg font-semibold text-primary">{plan.price || plan.title}</span>
              {plan.price ? <span className="font-body text-sm font-medium text-primary">{plan.title}</span> : null}
              <span className="font-body text-xs text-secondary">Auto-renews every {plan.duration}.</span>
              {plan.offerText ? <span className="mt-1 font-body text-[11px] text-secondary">{plan.offerText}</span> : null}
              {plan.trialText ? <span className="mt-1 font-body text-[11px] text-secondary">Intro offer details appear on the store confirmation sheet.</span> : null}
            </span>
            {!centered ? <span className="self-center font-body text-xs uppercase tracking-[0.18em] text-secondary">{plan.plan}</span> : null}
          </span>
        </button>
      ))}
      {showRestore ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void handleRestore()}
          className="w-full rounded-full px-4 py-3 border border-theme-secondary text-secondary font-body text-sm disabled:opacity-50"
        >
          {busy ? 'Please wait…' : 'Restore purchases'}
        </button>
      ) : null}
    </div>
  );
}